import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Small primitives for scroll-triggered reveals & masked hero lines.
 * Falls back to no motion when `prefers-reduced-motion`.
 */

export function Reveal({ children, delay = 0, y = 24, className, once = true, amount = 0.25, ...rest }) {
    const reduce = useReducedMotion();
    return (
        <motion.div
            className={className}
            initial={reduce ? false : { opacity: 0, y }}
            whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1], delay }}
            viewport={{ once, amount }}
            {...rest}
        >
            {children}
        </motion.div>
    );
}

export function MaskLine({ children, delay = 0, className, as: Tag = "span" }) {
    const reduce = useReducedMotion();
    return (
        <span className={`mask-lines block ${className || ""}`}>
            <motion.span
                initial={reduce ? false : { y: "110%", opacity: 0 }}
                animate={reduce ? undefined : { y: "0%", opacity: 1 }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay }}
                style={{ display: "block", willChange: "transform" }}
            >
                <Tag>{children}</Tag>
            </motion.span>
        </span>
    );
}

/** Stagger a set of children on scroll into view. */
export function Stagger({ children, className, gap = 0.09, y = 20, amount = 0.2 }) {
    const reduce = useReducedMotion();
    return (
        <motion.div
            className={className}
            initial={reduce ? false : "hidden"}
            whileInView={reduce ? undefined : "show"}
            viewport={{ once: true, amount }}
            variants={{
                hidden: {},
                show: { transition: { staggerChildren: gap, delayChildren: 0.05 } },
            }}
        >
            {React.Children.map(children, (child, i) => (
                <motion.div
                    key={i}
                    variants={{
                        hidden: { opacity: 0, y },
                        show: { opacity: 1, y: 0, transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
                    }}
                >
                    {child}
                </motion.div>
            ))}
        </motion.div>
    );
}
